import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import { DEFAULT_DASHBOARD_PAGE_SIZE } from '@/features/dashboards/constants/dashboard.constants';
import {
  expenseStatementSchema,
  specimenStatementSchema,
  zonalStatementResponseSchema,
  type ExpenseStatement,
  type SpecimenStatement,
  type ZonalStatementResponse,
  type ZonalStatementListSearch,
} from '../types/zonalStatement.types';

export const zonalStatementQueryKeys = {
  all: () => ['zonal-statement'] as const,
  list: (params: ZonalStatementListSearch) =>
    [...zonalStatementQueryKeys.all(), 'list', params] as const,
  expense: (userId: number, dateFrom?: string, dateTo?: string) =>
    [...zonalStatementQueryKeys.all(), 'expense', userId, dateFrom, dateTo] as const,
  specimen: (userId: number, dateFrom?: string, dateTo?: string) =>
    [...zonalStatementQueryKeys.all(), 'specimen', userId, dateFrom, dateTo] as const,
};

export const zonalStatementInfiniteQueryOptions = (params: ZonalStatementListSearch) =>
  infiniteQueryOptions({
    queryKey: zonalStatementQueryKeys.list(params),
    queryFn: async ({ pageParam }): Promise<ZonalStatementResponse> => {
      const res = await apiClient.get(API.zonalStatement.list, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_DASHBOARD_PAGE_SIZE },
      });
      return zonalStatementResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

interface StatementPeriod {
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
}

export const expenseStatementQueryOptions = (userId: number | null, period: StatementPeriod) =>
  queryOptions({
    queryKey: zonalStatementQueryKeys.expense(userId ?? 0, period.dateFrom, period.dateTo),
    queryFn: async (): Promise<ExpenseStatement> => {
      const res = await apiClient.get(API.zonalStatement.expenseStatement(userId ?? 0), {
        params: period,
      });
      return expenseStatementSchema.parse(res.data);
    },
    enabled: userId !== null,
    staleTime: STALE_TIME,
  });

export const specimenStatementQueryOptions = (userId: number | null, period: StatementPeriod) =>
  queryOptions({
    queryKey: zonalStatementQueryKeys.specimen(userId ?? 0, period.dateFrom, period.dateTo),
    queryFn: async (): Promise<SpecimenStatement> => {
      const res = await apiClient.get(API.zonalStatement.specimenStatement(userId ?? 0), {
        params: period,
      });
      return specimenStatementSchema.parse(res.data);
    },
    enabled: userId !== null,
    staleTime: STALE_TIME,
  });
